import React, { memo, useState, useEffect, useCallback } from "react";
import { PriceTools, CountryCode } from "@al/base-lib";
import cls from "classnames";
import { useT, useCtx, withRedux, WithReduxHocPropsType } from "@al/next/al-ex";

import { tracker } from "~/common/tracker";

import { InstallmentPlanItemType } from "../../../types";
import { SelectAttrType } from "../../../components/SkuSelect";
import { getTrackerSaReportInfo } from "../../../../sensor-config";

import styles from "./styles.scss";

interface Props extends WithReduxHocPropsType {
  installmentPlans: InstallmentPlanItemType[];
  selectAttrs?: SelectAttrType | null;
  onChange: (item: InstallmentPlanItemType) => void;
  skuAction: number;
}

const InstallmentPlans: React.FC<Props> = (props) => {
  const { installmentPlans, selectAttrs, onChange } = props;
  const t = useT();
  const countryCode = useCtx().locale.countryCode as CountryCode;

  const [selectPlan, setSelectPlan] = useState<InstallmentPlanItemType>();

  const onSelectPlan = useCallback(
    (item: InstallmentPlanItemType) => {
      onChange?.(item);
      setSelectPlan(item);
      if (item.periods) {
        tracker.popClick({
          sa: {
            ...getTrackerSaReportInfo({ page: "homePage", popName: "skuPop", eventName: "selectInstallment" }),
            skuAction: props.skuAction,
          },
        });
      } else {
        tracker.popClick({
          sa: {
            ...getTrackerSaReportInfo({ page: "homePage", popName: "skuPop", eventName: "selectFullPayment" }),
            skuAction: props.skuAction,
          },
        });
      }
    },
    [props.skuAction]
  );

  useEffect(() => {
    installmentPlans?.length && onSelectPlan(installmentPlans[0]);
  }, [installmentPlans]);

  // 没有选中 sku，以及没有分期的时候 都不显示 分期计划.
  if (!installmentPlans || installmentPlans?.length === 0 || selectAttrs?.item === undefined) {
    return null;
  }

  return (
    <div className={styles.payment_box}>
      <div className={styles.props_title}>{t("支付方式")}</div>
      <ul className={styles.payment_wrapper}>
        {installmentPlans?.map((item) => {
          return (
            <li
              className={cls(styles.payment_item, selectPlan?.periods === item.periods && styles.active)}
              key={item.orderPayType}
              onClick={(): void => onSelectPlan(item)}
            >
              <span className={styles.payment_item_tips}>
                {item.periods === 0 ? (
                  PriceTools.formatPrice(countryCode as CountryCode, item.downPayment, "price")
                ) : (
                  <>
                    <span>{PriceTools.formatPrice(countryCode as CountryCode, item.monthlyPay, "price")}</span>
                    <span> X</span>
                    <span> {t("{{x}}月", { x: item.periods })}</span>
                  </>
                )}
              </span>
              <span className={styles.payment_item_discount}>
                {item.periods === 0 ? (
                  t("全款")
                ) : item.interestCouponDiscount ? (
                  <span>
                    {t("获得折扣{{value}}", {
                      value: PriceTools.formatPrice(countryCode as CountryCode, item.interestCouponDiscount, "price"),
                    })}
                  </span>
                ) : (
                  ""
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <div className={styles.sub_tip}>{t("此页面金额仅为估计金额,实际金额以结算金额为准")}</div>
      {selectPlan?.periods ? (
        <div className={styles.down_payment}>
          <span className={styles.price}>{t("首付")}</span>
          <span className={styles.value}>
            {PriceTools.formatPrice(countryCode as CountryCode, selectPlan?.downPayment)}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default withRedux((state) => ({
  installmentPlans: state.static.installmentPlans,
  skuAction: state.static.skuAction,
}))(memo(InstallmentPlans));
